// components/admin/employees/CreateEmployee.jsx
"use client";
import { useState } from "react";
import { IoClose } from "react-icons/io5";
import { motion } from "framer-motion";
import { modalVariants } from "../../auth/signup/motion";
import { randomPassword } from "secure-random-password";
import { useEffect } from "react";
import { useSelector } from "react-redux";

const inputFields = [
  { name: "firstName", label: "First Name", type: "text" },
  { name: "lastName", label: "Last Name", type: "text" },
  { name: "email", label: "Email Address", type: "email" },
  { name: "phone", label: "Phone Number", type: "text" },
  { name: "country", label: "Country", type: "text" },
  { name: "username", label: "Username", type: "text" },
  { name: "password", label: "Password", type: "text" },
];

const FIELD_LIMITS = {
  firstName: 100,
  lastName: 100,
  email: 100,
  phone: 15,
  country: 50,
  username: 30,
  password: 25,
};

const PHONE_MIN_LENGTH = 7;
const ALPHABETIC_FIELDS = ["firstName", "lastName", "country"];

const teams = [
  "GTE & PR Team",
  "Admissions Team",
  "A&F Team",
  "Visa/Immi Team",
  "Customer Support",
  "Onshore Admissions Team",
  "Media Team",
  "Skill Assessment",
  "Assignments",
  "MEGA Upload",
  "USA DS-160 Team",
  "USA Account Opening Team",
  "Management Team",
  // "Mandi Non Operational Staff",
];

export default function CreateEmployeeForm({
  isModalOpen,
  setIsModalOpen,
  onSubmit,
  error: serverError,
  agent = null, // Add this prop for edit mode
  isEdit = false, // Add this prop to distinguish between create and edit
}) {
  const [localError, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    firstName: agent?.firstName || "",
    lastName: agent?.lastName || "",
    email: agent?.email || "",
    phone: agent?.phone || "",
    country: agent?.country || "",
    team: agent?.team || "",
    username: agent?.username || "",
    password: agent?.password || "",
    role: agent?.role || "agent",
  });

  const { isCreating } = useSelector((state) => state.agents);

  const [formSubmitted, setFormSubmitted] = useState(false);

  const sanitizeInputValue = (fieldName, value) => {
    if (fieldName === "phone") {
      return value.replace(/\D/g, "").slice(0, FIELD_LIMITS.phone);
    }

    if (ALPHABETIC_FIELDS.includes(fieldName)) {
      return value
        .replace(/[^A-Za-z\s]/g, "")
        .replace(/\s{2,}/g, " ")
        .slice(0, FIELD_LIMITS[fieldName]);
    }

    if (fieldName === "username") {
      return value.replace(/\s/g, "").slice(0, FIELD_LIMITS.username);
    }

    const maxLength = FIELD_LIMITS[fieldName];
    if (!maxLength) return value;
    return value.slice(0, maxLength);
  };

  const isFieldRequired = (fieldName) => {
    if (fieldName === "password") return !isEdit;
    return [
      "firstName",
      "lastName",
      "email",
      "phone",
      "country",
      "team",
      "username",
    ].includes(fieldName);
  };

  const validateField = (fieldName, value, sourceData = formData) => {
    const normalizedValue = typeof value === "string" ? value.trim() : value;

    if (isFieldRequired(fieldName) && !normalizedValue) {
      return `${inputFields.find((f) => f.name === fieldName)?.label || "This field"} is required.`;
    }

    if (ALPHABETIC_FIELDS.includes(fieldName) && normalizedValue) {
      const alphabeticRegex = /^[A-Za-z\s]+$/;
      if (!alphabeticRegex.test(normalizedValue)) {
        const fieldLabel = inputFields.find((f) => f.name === fieldName)?.label || "This field";
        return `${fieldLabel} should contain only alphabetic characters.`;
      }
    }

    if (fieldName === "email" && normalizedValue) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(normalizedValue)) {
        return "Invalid email format.";
      }
    }

    if (fieldName === "phone" && normalizedValue) {
      if (normalizedValue.length < PHONE_MIN_LENGTH) {
        return `Phone number must be at least ${PHONE_MIN_LENGTH} digits.`;
      }
      if (normalizedValue.length > FIELD_LIMITS.phone) {
        return `Phone number cannot exceed ${FIELD_LIMITS.phone} digits.`;
      }
    }

    if (fieldName === "country" && normalizedValue.length > FIELD_LIMITS.country) {
      return `Country cannot exceed ${FIELD_LIMITS.country} characters.`;
    }

    if (fieldName === "username" && normalizedValue.length > FIELD_LIMITS.username) {
      return `Username cannot exceed ${FIELD_LIMITS.username} characters.`;
    }

    if (fieldName === "password" && normalizedValue) {
      if (normalizedValue.length < 8) {
        return "Password must be at least 8 characters long.";
      }

      if (normalizedValue.length > FIELD_LIMITS.password) {
        return `Password cannot exceed ${FIELD_LIMITS.password} characters.`;
      }

      const hasLetter = /[a-zA-Z]/.test(normalizedValue);
      const hasNumber = /[0-9]/.test(normalizedValue);

      if (!hasLetter || !hasNumber) {
        return "Password must contain both letters and numbers.";
      }
    }

    if (fieldName === "team" && !normalizedValue) {
      return "Team is required.";
    }

    return "";
  };

  useEffect(() => {
    // Only generate password if not in edit mode
    if (!isEdit) {
      const password = randomPassword({
        length: 12,
        characters:
          "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
      });
      setFormData((prev) => ({ ...prev, password }));
    }
  }, []);

  useEffect(() => {
    if (!isModalOpen) {
      setFormSubmitted(false);
      setError("");
      setFieldErrors({});
      setFormData({
        firstName: agent?.firstName || "",
        lastName: agent?.lastName || "",
        email: agent?.email || "",
        phone: agent?.phone || "",
        country: agent?.country || "",
        team: agent?.team || "",
        username: agent?.username || "",
        password: agent?.password || "",
        role: agent?.role || "agent",
      });

      // Only generate password if not in edit mode
      if (!isEdit) {
        const password = randomPassword({
          length: 12,
          characters:
            "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789",
        });
        setFormData((prev) => ({ ...prev, password }));
      }
    }
  }, [isModalOpen, isEdit, agent]);

  const validateForm = () => {
    const nextErrors = {};
    const fieldsToValidate = [
      "firstName",
      "lastName",
      "email",
      "phone",
      "country",
      "team",
      "username",
      "password",
    ];

    fieldsToValidate.forEach((fieldName) => {
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        nextErrors[fieldName] = error;
      }
    });

    setFieldErrors(nextErrors);
    setError(Object.keys(nextErrors).length ? "Please correct the highlighted fields." : "");

    return Object.keys(nextErrors).length === 0;
  };

  useEffect(() => {
    if (!formSubmitted) return;

    const nextErrors = {};
    Object.keys(formData).forEach((fieldName) => {
      if (fieldName === "role") return;
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        nextErrors[fieldName] = error;
      }
    });

    setFieldErrors(nextErrors);
    setError(Object.keys(nextErrors).length ? "Please correct the highlighted fields." : "");
  }, [formData, formSubmitted, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);

    if (validateForm()) {
      // Don't allow editing email and role in edit mode
      // Also remove password if it's empty in edit mode
      if (isEdit) {
        const { email, role, ...updateData } = formData;
        
        // If password is empty in edit mode, don't include it
        if (!updateData.password) {
          delete updateData.password;
        }
        
        onSubmit(updateData);
      } else {
        onSubmit(formData);
      }
    }
  };

  if (!isModalOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed top-0 left-0 h-screen w-screen flex items-center justify-center bg-black bg-opacity-50 z-50"
    >
      <motion.div
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="bg-white rounded-lg w-full max-w-md relative overflow-y-auto max-h-[90vh] z-[70] shadow-lg"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">
              {isEdit ? "Edit Employee" : "Add Employee"}
            </h2>
            <button
              onClick={() => setIsModalOpen(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <IoClose size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-2">
            {inputFields.map((field) =>
              field.name !== "team" ? (
                <div key={field.name} className="space-y-1">
                    <label className="block text-gray-700 text-sm">
                      {field.label}
                      {isFieldRequired(field.name) && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                      {field.name === "password" && isEdit && (
                        <span className="text-gray-500 ml-1">(optional)</span>
                      )}
                    </label>
                  <input
                    type={field.type}
                    value={formData[field.name]}
                    onChange={(e) =>
                      setFormData((prev) => {
                        const updatedData = {
                          ...prev,
                          [field.name]: sanitizeInputValue(field.name, e.target.value),
                        };

                        if (formSubmitted) {
                          const fieldError = validateField(
                            field.name,
                            updatedData[field.name],
                            updatedData,
                          );
                          setFieldErrors((prevErrors) => ({
                            ...prevErrors,
                            [field.name]: fieldError,
                          }));
                        }

                        return updatedData;
                      })
                    }
                    maxLength={FIELD_LIMITS[field.name]}
                    minLength={field.name === "phone" ? PHONE_MIN_LENGTH : undefined}
                    inputMode={field.name === "phone" ? "numeric" : undefined}
                    pattern={
                      field.name === "phone"
                        ? "[0-9]*"
                        : ALPHABETIC_FIELDS.includes(field.name)
                          ? "[A-Za-z ]*"
                          : undefined
                    }
                    readOnly={isEdit && (field.name === "email" || field.name === "role")}
                    placeholder={field.name === "password" && isEdit ? "Leave blank to keep current password" : ""}
                    className={`w-full px-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-[#BBBBBB] bg-[#F0F0F0] text-sm appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
                      isEdit && (field.name === "email" || field.name === "role") ? "bg-gray-200" : ""
                    }`}
                  />
                  {fieldErrors[field.name] && (
                    <p className="text-xs text-red-500">{fieldErrors[field.name]}</p>
                  )}
                </div>
              ) : null
            )}

            <div className="space-y-1">
                <label className="block text-gray-700 text-sm">
                  Team
                  <span className="text-red-500 ml-1">*</span>
                </label>
              <select
                value={formData.team}
                onChange={(e) =>
                  setFormData((prev) => {
                    const updatedData = { ...prev, team: e.target.value };

                    if (formSubmitted) {
                      const teamError = validateField("team", updatedData.team, updatedData);
                      setFieldErrors((prevErrors) => ({
                        ...prevErrors,
                        team: teamError,
                      }));
                    }

                    return updatedData;
                  })
                }
                className="w-full px-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-[#BBBBBB] bg-[#F0F0F0] text-sm"
              >
                <option value="">Select a Team</option>
                {teams.map((team) => (
                  <option key={team} value={team}>
                    {team}
                  </option>
                ))}
              </select>
              {fieldErrors.team && (
                <p className="text-xs text-red-500">{fieldErrors.team}</p>
              )}
            </div>

            <div className="pt-1">
              {localError && (
                <p className="text-sm text-red-500 mb-1">{localError}</p>
              )}
              {serverError && (
                <p className="text-sm text-red-500 mb-1">{serverError}</p>
              )}

              <button
                type="submit"
                disabled={isCreating}
                className="w-full py-2 text-white bg-[#036494] rounded-full hover:bg-[#025177] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isCreating ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    {isEdit ? "Updating..." : "Creating..."}
                  </div>
                ) : (
                  isEdit ? "Update" : "Add"
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
}